import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";

import Spending from "./Spending.jsx";
import {
  Clock,
  DrawerHead,
  Figure,
  Figures,
  MODELS,
  Markdown,
  Term,
  Tooltip,
  modelChoice,
  readClock,
} from "@lopie/kernel-ui";

/**
 * One agent's controls, with room to breathe.
 *
 * ## Why this is a drawer and not a form on a card
 *
 * The first version put the cadence, two time fields, a timezone select and
 * two buttons inside a card in a three-column grid. Nine controls in three
 * hundred pixels, wrapping at every width, and a `Save` that sat below the
 * fold of the thing it saved. It was cramped because the space was wrong, not
 * because the controls were.
 *
 * A drawer is the surface this product already uses for *the record behind a
 * card* — the ceiling, the configuration, the writes — and a control is the
 * same kind of thing: something you came here specifically to do. The card
 * summarises and the drawer acts.
 *
 * ## The off switch is first and it is not a setting
 *
 * The case that produced it is an agent costing $19.14 over five runs and
 * writing nothing. Somebody reading that is not looking for a cadence, they
 * are looking for a way to stop it, and that control should be the first
 * thing under the name rather than the last thing under a schedule.
 *
 * It is set apart and it carries the consequence in words — what stops, what
 * does not, and that it is reversible in one click. A destructive-looking
 * control that is actually reversible should say so, or people will not use
 * it when they should.
 */

const ZONES = [
  "UTC",
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Singapore",
  "Australia/Sydney",
];

/** How hard it thinks. The lever that moves cost most for a given model. */
const EFFORTS = ["low", "medium", "high"];

const CADENCES = [
  [60, "Every hour"],
  [180, "Every 3 hours"],
  [360, "Every 6 hours"],
  [720, "Every 12 hours"],
  [1440, "Every day"],
  [10080, "Every week"],
];

const count = (n) => Number(n || 0).toLocaleString("en-US");

function dollars(usd) {
  if (!usd) return "$0.00";
  return usd < 0.01 ? "<$0.01" : `$${usd.toFixed(2)}`;
}

/**
 * A heading with a rule, and the sentence that says what the section is.
 *
 * The rule alone was enough when the only readers were the people who built
 * it. `Running`, `When it runs`, `What it is` are three labels that each make
 * sense once you know what they refer to and none of which tell you — so the
 * note is not optional decoration, it is the part a new person reads.
 */
function Heading({ children, note }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        variant="overline"
        sx={{
          display: "block",
          pb: 0.5,
          borderBottom: 1,
          borderColor: "divider",
          color: "text.primary",
        }}
      >
        {children}
      </Typography>
      {note ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, maxWidth: "62ch" }}
        >
          {note}
        </Typography>
      ) : null}
    </Box>
  );
}

export default function AgentControls({
  agent,
  onClose,
  onSchedule,
  onSuspend,
  onConfigure,
  onBudget,
}) {
  const [every, setEvery] = useState(1440);
  const [enabled, setEnabled] = useState(false);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [zone, setZone] = useState("UTC");
  const [reason, setReason] = useState("");
  const [model, setModel] = useState("");
  const [effort, setEffort] = useState("");
  const [prompt, setPrompt] = useState("");
  // Edit or read. A textarea cannot render Markdown, and these
  // prompts are Markdown — so the choice is a toggle rather than a
  // compromise that does neither well.
  const [reading, setReading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);

  // Seeded from the kernel when the panel opens on a different agent — and
  // deliberately **not** on every new `agent` object.
  //
  // The screen behind this polls every thirty seconds, and each poll hands
  // down a fresh object for the same agent. Depending on identity meant that
  // poll re-ran this effect and overwrote whatever was half-typed: pick
  // `high`, wait, and the select silently went back to `medium` with `Save`
  // greyed out again. Depending on the name instead means a draft survives
  // until it is saved or discarded, and `changed` still compares against the
  // freshly-loaded `agent.config` — so a successful save disables `Save` on
  // its own, without this effect having to fire at all.
  useEffect(() => {
    setEnabled(Boolean(agent?.schedule?.enabled));
    setEvery(agent?.schedule?.every_minutes || 1440);
    setStart(agent?.schedule?.window_start || "");
    setEnd(agent?.schedule?.window_end || "");
    setZone(agent?.schedule?.timezone || "UTC");
    setReason("");
    setModel(agent?.config?.model || "");
    setEffort(agent?.config?.effort || "");
    setPrompt(agent?.config?.system || "");
    setFailure(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.name]);

  if (!agent) return <Drawer anchor="right" open={false} onClose={onClose} />;

  const off = Boolean(agent.suspension);
  // Nothing is sent that did not change. A save that posted an unchanged
  // prompt would clear `version` and mark this kernel ahead of Anthropic for
  // no reason, which is a drift warning somebody then has to explain.
  const changed =
    Boolean(agent.config)
    && (model !== (agent.config.model || "")
      || effort !== (agent.config.effort || "")
      || prompt !== (agent.config.system || ""));
  const windowed = Boolean(start && end);
  const wraps = windowed && start > end;
  const spend = agent.spend;

  const run = async (fn) => {
    setBusy(true);
    setFailure(null);
    try {
      await fn();
    } catch (e) {
      // The kernel's own sentence. It refuses a cadence under an hour, a
      // window with one end, a zero-width window and a timezone it cannot
      // resolve, and names which — so restating them here would be a second
      // copy that drifts from the one being enforced.
      setFailure(e?.body?.detail || e?.message || "The kernel refused that.");
    } finally {
      setBusy(false);
    }
  };

  const saveSchedule = (over = {}) =>
    run(() =>
      onSchedule({
        enabled,
        every_minutes: every,
        window_start: start,
        window_end: end,
        timezone: zone,
        ...over,
      }),
    );

  return (
    <Drawer
      anchor="right"
      open={Boolean(agent)}
      onClose={onClose}
      slotProps={{
        paper: { sx: { width: { xs: "100%", sm: 560 }, maxWidth: "100%" } },
      }}
    >
      <Box sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
        <DrawerHead
          title={agent.label || agent.name}
          id={`${agent.name} · ${agent.app_type}`}
          onClose={onClose}
          lede={
            <>
              {agent.does} Everything on this panel changes how this{" "}
              <Term name="agent">agent</Term> behaves from now on — whether it
              runs at all, when it is allowed to start, and what it was told to
              do. None of it can widen what the agent is permitted to touch;
              that is set by a reviewed file and changing it is a separate,
              slower thing.
            </>
          }
        />

        <Figures sx={{ mb: 3 }}>
          <Figure
            value={dollars(spend?.usd || 0)}
            label="spent so far"
            basis={
              spend?.basis
              || "Priced at Anthropic's published rates. Not an invoice."
            }
          />
          <Figure
            value={count(spend?.runs || 0)}
            label="times it has run"
            basis="Runs this kernel started. Anything driven from a terminal before this existed is not counted."
          />
          <Figure
            value={spend?.last_run_usd ? `$${spend.last_run_usd.toFixed(4)}` : "—"}
            label="the last one cost"
          />
        </Figures>

        {/* ## The off switch, first
            Set apart, and it carries the consequence in words. A control that
            looks destructive and is actually one click to undo should say so,
            or people will not reach for it when they ought to. */}
        <Heading note="The master switch. Off means nothing starts this agent at all — not the clock, not a person pressing a button — and it costs nothing while it is off. It stays declared and keeps its permissions, so switching it back on is this one control.">
          Running
        </Heading>
        <Box
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 1,
            border: 1,
            borderColor: off ? "kernel.attention" : "divider",
            backgroundColor: off ? "action.hover" : "transparent",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {off ? "Switched off" : "On"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {off
                  ? "Nothing will start it — not the ticker, not a person"
                  : "The ticker may start it, and so may a person"}
              </Typography>
            </Box>
            <Switch
              checked={!off}
              disabled={busy || !onSuspend}
              // `slotProps`, not `inputProps`. The v5 prop is accepted, React
              // does not warn, and the label simply never reaches the input —
              // so the master switch on this panel had no accessible name and
              // a screen reader announced it as an unlabelled checkbox. The
              // give-away is `getAttribute("aria-label")` coming back null on
              // a control that reads as labelled in the source.
              slotProps={{ input: { "aria-label": "Agent is running" } }}
              onChange={(e) =>
                run(() =>
                  onSuspend({
                    suspended: !e.target.checked,
                    reason: e.target.checked ? "" : reason,
                  }),
                )
              }
            />
          </Stack>

          {off ? (
            <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
              Switched off{agent.suspension.by ? ` by ${agent.suspension.by}` : ""}
              {agent.suspension.at ? ` on ${agent.suspension.at.slice(0, 10)}` : ""}
              {agent.suspension.reason ? ` — ${agent.suspension.reason}` : ""}.
              It is still declared and its ceiling is unchanged, so turning it
              back on is this switch and nothing else.
            </Alert>
          ) : (
            <TextField
              size="small"
              fullWidth
              label="Why, if you switch it off"
              placeholder="costs a lot, does a little"
              value={reason}
              disabled={busy || !onSuspend}
              onChange={(e) => setReason(e.target.value)}
              sx={{ mt: 1.5 }}
              helperText="Optional, and worth it. An agent switched off six weeks ago with no reason is one nobody dares switch back on."
            />
          )}
        </Box>

        <Heading note="Two separate things. How often it starts on its own, and the hours of the day it is allowed to start in — so an agent can run every hour and still never run overnight.">
          When it runs
        </Heading>
        <Stack spacing={2} sx={{ mb: 1, opacity: off ? 0.5 : 1 }}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Tooltip
              title={
                enabled
                  ? "It will start itself at the interval beside this, inside the hours below."
                  : "It runs only when somebody starts it. That is true of most agents and is not a problem."
              }
            >
              <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                <Switch
                  size="small"
                  checked={enabled}
                  disabled={busy || off || !onSchedule}
                  slotProps={{
                    input: { "aria-label": "Runs on a schedule" },
                  }}
                  onChange={(e) => {
                    setEnabled(e.target.checked);
                    saveSchedule({ enabled: e.target.checked });
                  }}
                />
                <Typography variant="body2">On a schedule</Typography>
              </Stack>
            </Tooltip>

            <TextField
              select
              size="small"
              label="How often"
              value={every}
              disabled={busy || off || !enabled || !onSchedule}
              onChange={(e) => {
                setEvery(Number(e.target.value));
                saveSchedule({ every_minutes: Number(e.target.value) });
              }}
              sx={{ minWidth: 170 }}
            >
              {CADENCES.map(([minutes, label]) => (
                <MenuItem key={minutes} value={minutes}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
              Only between these hours
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Leave both blank and it may start at any hour.
            </Typography>
            {/* Two rows, not one. Three selects is about 250px and two of
                them plus the word between them is 530 — which is wider than
                this drawer's content box, so the row wrapped mid-pair and put
                the start time and the end time on separate lines with nothing
                saying which was which. Stacked and labelled, it fits at every
                width and reads as a range. */}
            <Stack spacing={1.5}>
              <Stack spacing={1}>
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 48 }}
                  >
                    From
                  </Typography>
                  <Clock
                    value={start}
                    label="window start"
                    disabled={busy || off || !enabled || !onSchedule}
                    onChange={setStart}
                  />
                </Stack>
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: 48 }}
                  >
                    Until
                  </Typography>
                  <Clock
                    value={end}
                    label="window end"
                    disabled={busy || off || !enabled || !onSchedule}
                    onChange={setEnd}
                  />
                </Stack>
              </Stack>
              {/* The caption is a sibling rather than `helperText`, because
                  helper text is laid out at the *field's* width — and a
                  two-line sentence under a 260px select renders as a tall
                  narrow column of four words per line. The field stays the
                  width a timezone needs; the sentence gets the drawer. */}
              <Box>
                <TextField
                  select
                  size="small"
                  label="Timezone"
                  value={zone}
                  disabled={busy || off || !enabled || !onSchedule}
                  onChange={(e) => setZone(e.target.value)}
                  sx={{ minWidth: 260 }}
                >
                  {ZONES.map((z) => (
                    <MenuItem key={z} value={z}>
                      {z}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.75, maxWidth: "62ch" }}
                >
                  The hours above are read in this timezone. Pick the wrong one
                  and it runs at the wrong time of day while looking perfectly
                  healthy.
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
              <Button
                variant="contained"
                size="small"
                disabled={busy || off || !enabled || !onSchedule}
                onClick={() => saveSchedule()}
              >
                Save window
              </Button>
              {windowed ? (
                <Button
                  size="small"
                  disabled={busy || off || !onSchedule}
                  onClick={() => {
                    setStart("");
                    setEnd("");
                    saveSchedule({ window_start: "", window_end: "" });
                  }}
                >
                  Any hour
                </Button>
              ) : null}
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              {windowed
                ? wraps
                  ? `Overnight — it may start after ${readClock(start)} and before ${readClock(end)} the next morning. A run that begins inside the window and finishes outside it is not cut short.`
                  : `It may only start between ${readClock(start)} and ${readClock(end)}.`
                : "Any hour. Set both ends to hold it to a window."}
            </Typography>
          </Box>
        </Stack>

        {failure ? (
          <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
            {failure}
          </Alert>
        ) : null}

        <Box sx={{ mt: 3 }}>
          <Heading note="Two numbers. The first flags it and changes nothing; the second refuses every run. An agent between them keeps working and reads as needing a decision, which is where most budgets spend most of their life.">
            Spending
          </Heading>
          <Spending agent={agent} onBudget={onBudget} busy={busy} />
        </Box>

        {agent.lateness?.state === "waiting" && !off ? (
          <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
            {agent.lateness.detail}
          </Alert>
        ) : null}

        {/* ## The other half of an agent, and it is editable here
            `manifests/<name>.json` says what the *kernel* will let this app
            touch; this says what *Anthropic* will let the agent do — the
            model it thinks with, the prompt it was given, the tools it was
            handed. LO-110 built `POST /agents/{app_type}/config` and nothing
            in any interface called it, so a prompt was a thing you could read
            in the repo and change nowhere.

            **It cannot widen a ceiling, and that is why it can be a form.**
            The route writes `#CONFIG` and `resolve_manifest` never reads
            `#CONFIG`, so a tool added here that the manifest does not permit
            is refused on every call. Editing a prompt from a screen is safe
            in a way editing a manifest from a screen would not be. */}
        <Box sx={{ mt: 3 }}>
          <Heading
            note={
              <>
                The model it thinks with, how hard it thinks, and the
                instructions it was given. Changing these changes how it does
                its job; none of them change what it is allowed to reach, so
                there is nothing you can widen by accident here.
              </>
            }
          >
            How it thinks
          </Heading>

          {!agent.config ? (
            <Typography variant="body2" color="text.secondary">
              Nothing to show yet. This agent has been declared and permitted,
              but the part that says how it thinks — its model and its
              instructions — has never been loaded into this kernel, so there
              is nothing here to read or change. Whoever set the agent up
              needs to publish it once.
            </Typography>
          ) : (
            <Stack spacing={2} sx={{ opacity: off ? 0.5 : 1 }}>
              <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", rowGap: 2 }}>
                <TextField
                  select
                  size="small"
                  label="Model"
                  value={model}
                  disabled={busy || off || !onConfigure}
                  onChange={(e) => setModel(e.target.value)}
                  sx={{ minWidth: 300 }}
                  helperText="What it thinks with. Cheaper models cost less per run and are worse at hard judgement."
                >
                  {/* A model already in force that is not on the list stays
                      selectable rather than being silently swapped on first
                      render — the same rule `Clock` uses for an off-grid
                      minute. Changing somebody's setting by opening a panel
                      is not an acceptable side effect. */}
                  {(MODELS.some(([id]) => id === model)
                    ? MODELS.map(([id]) => id)
                    : [model, ...MODELS.map(([id]) => id)]
                  ).map((id) => (
                    <MenuItem key={id} value={id}>
                      {modelChoice(id)}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  size="small"
                  label="How hard it thinks"
                  value={effort}
                  disabled={busy || off || !onConfigure}
                  onChange={(e) => setEffort(e.target.value)}
                  sx={{ minWidth: 190 }}
                  helperText="Higher costs more and is not automatically better."
                >
                  {(EFFORTS.includes(effort) ? EFFORTS : [effort, ...EFFORTS]).map(
                    (e) => (
                      <MenuItem key={e} value={e}>
                        {e}
                      </MenuItem>
                    ),
                  )}
                </TextField>
              </Stack>

              {/* ## Read by default, edit on purpose
                  These prompts are three thousand characters of Markdown —
                  headings, a numbered procedure, bold run-in rules — and a
                  textarea shows somebody the `#` and the `**` rather than
                  the structure that makes any of it scannable. So the
                  default is the rendered thing, and editing is a deliberate
                  switch rather than the state you land in. */}
              <Box>
                <Stack
                  direction="row"
                  sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}
                >
                  <Typography variant="overline" color="text.secondary">
                    Its instructions
                  </Typography>
                  <Tabs
                    value={reading ? 0 : 1}
                    onChange={(e, v) => setReading(v === 0)}
                    sx={{ minHeight: 0, "& .MuiTab-root": { minHeight: 0, py: 0.75 } }}
                  >
                    <Tab label="Read" />
                    <Tab label="Edit" disabled={busy || off || !onConfigure} />
                  </Tabs>
                </Stack>

                {reading ? (
                  <Box
                    sx={{
                      p: 1.5,
                      maxHeight: 420,
                      overflow: "auto",
                      bgcolor: "action.hover",
                      borderRadius: 1,
                      border: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Markdown>{prompt}</Markdown>
                  </Box>
                ) : (
                  <TextField
                    multiline
                    minRows={10}
                    maxRows={24}
                    fullWidth
                    value={prompt}
                    disabled={busy || off || !onConfigure}
                    onChange={(e) => setPrompt(e.target.value)}
                    slotProps={{ htmlInput: { spellCheck: false } }}
                    sx={{
                      "& textarea": {
                        fontFamily: "ui-monospace, monospace",
                        fontSize: "0.8125rem",
                        lineHeight: 1.6,
                      },
                    }}
                  />
                )}

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.75, maxWidth: "62ch" }}
                >
                  {prompt.length.toLocaleString()} characters. These are the
                  standing instructions the agent gets on every run. They
                  decide what it does and how; they cannot give it access to
                  anything it is not already permitted to reach.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", rowGap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={busy || off || !onConfigure || !changed}
                  onClick={() =>
                    run(() =>
                      onConfigure({ model, effort, system: prompt }),
                    )
                  }
                >
                  Save changes
                </Button>
                {changed ? (
                  <Button
                    size="small"
                    disabled={busy}
                    onClick={() => {
                      setModel(agent.config.model || "");
                      setEffort(agent.config.effort || "");
                      setPrompt(agent.config.system || "");
                    }}
                  >
                    Discard
                  </Button>
                ) : null}
                {(agent.config.tools || []).map((t) => (
                  <Chip
                    key={t.name || t.type}
                    size="small"
                    variant="outlined"
                    label={t.name || t.type}
                    sx={{ fontFamily: "ui-monospace, monospace" }}
                  />
                ))}
              </Stack>

              {/* Tools are shown and not edited. What a tool may reach is the
                  manifest's decision, and a tool list on a screen beside a
                  prompt invites somebody to believe adding one grants
                  something — it does not, and the honest place to change it
                  is the committed definition. */}
              <Typography variant="caption" color="text.secondary">
                Those chips are the tools this agent has been handed. They are
                shown and not edited here on purpose: what a tool is allowed to
                reach is decided by the agent&apos;s reviewed permissions, so
                one added on this screen would simply be refused every time it
                was used.
              </Typography>

              {agent.config.version ? (
                <Typography variant="caption" color="text.secondary">
                  Live. Anthropic is running version {agent.config.version} of
                  this definition
                  {agent.config.applied_by
                    ? `, published by ${agent.config.applied_by}`
                    : ""}
                  .
                </Typography>
              ) : (
                <Alert severity="warning" variant="outlined">
                  <strong>Saved here, not live yet.</strong> What you see on
                  this panel has not been pushed out to Anthropic, so the agent
                  is still running with its previous instructions. Saving
                  records the change; a separate publish step makes it take
                  effect.
                </Alert>
              )}
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
